<template>
    <div class="order-list-page">
        <van-list
            v-model="loading"
            :finished="finished"
            finished-text="没有更多了"
            @load="onLoad"
        >
            <div v-if="orders.length" class="list-wrap">
                <div class="order-card" v-for="item in orders" :key="item.id">
                    <div class="card-top">
                        <div class="left-title">
                            <van-icon name="orders-o" color="#3fd0e6" size="18" />
                            <span>{{ item.type }}</span>
                        </div>
                        <span class="status" :class="{ unpaid: !item.invoiced }">{{
                            item.invoiced ? '已开票' : '未开票'
                        }}</span>
                    </div>

                    <div class="line">
                        {{ item.name }}&nbsp;&nbsp;&nbsp;{{ item.phone || '--'
                        }}&nbsp;&nbsp;&nbsp;{{ item.outTradeNo || '—' }}
                    </div>
                    <div class="course-name">{{ item.title }}</div>

                    <div class="card-bottom">
                        <div class="amount-wrap">
                            <span class="label">支付金额</span>
                            <span class="amount">{{ item.amount }}</span>
                            <span class="date">{{ formatCreateTime(item.createTime) }}</span>
                        </div>
                        <van-button
                            v-if="item.invoiceUrl"
                            type="info"
                            size="small"
                            round
                            @click="openInvoice(item)"
                            >查看发票</van-button
                        >
                        <van-button
                            v-else-if="item.invoiced"
                            type="info"
                            size="small"
                            round
                            plain
                            @click="$toast(item.invoicePlain || '发票链接暂未下发')"
                            >查看发票</van-button
                        >
                    </div>
                </div>
            </div>
        </van-list>

        <div v-if="!loading && finished && orders.length === 0" class="empty-wrap">
            <van-empty description="暂无订单" />
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { getPayOrder } from '@/api/index'
import { getUserInfoCache } from '@/utils/auth'

export default {
    name: 'OrderList',
    data() {
        return {
            orders: [],
            pageNum: 1,
            pageSize: 10,
            loading: false,
            finished: false,
            total: 0,
            userName: ''
        };
    },
    created() {
        const u = getUserInfoCache()
        this.userName = (u && u.name) ? u.name : ''
        /** van-list 在首屏/空列表时可能不触发 @load，首次请求与 knowledge/index.vue 一致在 created 触发 */
        this.loadOrders(false)
    },
    methods: {
        onLoad() {
            this.loadOrders(true)
        },
        /** isLoadMore：是否追加分页（与评测列表页一致） */
        loadOrders(isLoadMore = false) {
            if (this.loading || this.finished) return

            this.loading = true
            const payload = {
                pageNum: this.pageNum,
                pageSize: this.pageSize
            }

            getPayOrder(payload).then(res => {
                const records = res.data?.records || []
                const total = Number(res.data?.total ?? res.data?.totalCount ?? 0)

                if (isLoadMore) {
                    this.orders = [...this.orders, ...records]
                } else {
                    this.orders = records
                }

                this.total = total
                if (this.orders.length >= total || records.length < this.pageSize) {
                    this.finished = true
                } else {
                    this.pageNum += 1
                }
                this.loading = false
            }).catch(() => {
                this.loading = false
                this.finished = true
            })
        },
        /** 与 /h5/getPayOrder 字段一致：amount、invoice、invoiceState、outTradeNo、successTime、createTime、medicalName、name、title、type … */
        formatCreateTime(value) {
            if (value === undefined || value === null || String(value).trim() === '') {
                return '--'
            }
            const m = moment(value)
            if (!m.isValid()) return String(value)
            return m.format('YYYY-MM-DD HH:mm')
        },
        formatOrderAmount(raw) {
            const amt = raw.amount
            if (amt === undefined || amt === null || amt === '') {
                return '--'
            }
            const n = Number(amt)
            if (!Number.isFinite(n)) {
                const s = String(amt).trim()
                return s.includes('元') ? s : `${s}元`
            }
            const str = n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2).replace(/\.00$/, '')
            return `${str}元`
        },
        resolveInvoiced(raw) {
            const state = raw.invoiceState
            if (state !== undefined && state !== null && String(state).trim() !== '') {
                const s = String(state).trim()
                const num = Number(s)
                if (!Number.isNaN(num)) {
                    if (num === 1 || num === 2) return true
                    if (num === 0) return false
                }
                if (/未开|WAIT|NONE|FAILED/i.test(s)) return false
                if (/已开|SUCCESS|COMPLETE|DONE|开票|ISSUED/i.test(s)) return true
            }
            const inv = (raw.invoice || '').trim()
            if (inv) {
                if (/^https?:\/\//i.test(inv)) return true
                return true
            }
            return false
        },
        /** 第三列表格展示：发票号/外链摘要 */
        formatInvoiceLineText(invoiceField) {
            if (!invoiceField) return ''
            if (/^https?:\/\//i.test(invoiceField)) return '电子发票'
            return invoiceField
        },
        normalizeOrderRow(raw, fallbackIndex = 0) {
            const oid = raw.id
            const id =
                oid !== undefined && oid !== null
                    ? `order-${oid}-${(raw.outTradeNo || '').trim() || fallbackIndex}`
                    : `order-${fallbackIndex}-${(raw.outTradeNo || '').trim() || 'row'}`
            const orderNo = (raw.outTradeNo ?? '').trim()
            const invoiceField = raw.invoice != null ? String(raw.invoice).trim() : ''

            const invoiced = this.resolveInvoiced(raw)
            let invoiceUrl = ''
            if (invoiceField && /^https?:\/\//i.test(invoiceField)) {
                invoiceUrl = invoiceField
            }

            const paidAt = raw.successTime || raw.createTime

            const medicalName = (raw.medicalName ?? '').trim()
            const titleDisp = (raw.title ?? '').trim()
            const typeStr = (raw.type ?? '').trim()

            const isInvoiceUrl = invoiceField && /^https?:\/\//i.test(invoiceField)
            const invoicePlain =
                invoiceField && !isInvoiceUrl ? invoiceField : ''

            return {
                id,
                typeLabel: typeStr || titleDisp || medicalName || '订单',
                titleDisplay: titleDisp,
                orderNo,
                invoiceText: this.formatInvoiceLineText(invoiceField),
                invoicePlain,
                medicalName,
                customerName: (raw.name ?? '').trim(),
                amount: this.formatOrderAmount(raw),
                date: paidAt ? moment(paidAt).format('YYYY年MM月DD日') : '--',
                invoiced,
                invoiceUrl
            }
        },
        openInvoice(item) {
            const url = item.invoiceUrl
            if (!url) {
                if (item.invoicePlain) {
                    this.$toast(`发票信息：${item.invoicePlain}`)
                } else {
                    this.$toast('暂无发票链接')
                }
                return
            }
            window.location.href = url
        }
    }
};
</script>

<style scoped lang="scss">
.order-list-page {
    min-height: 100vh;
    background: #eef7f8;
    padding: 12px;
    box-sizing: border-box;
}

.list-wrap {
    padding-bottom: 8px;
}

.empty-wrap {
    padding: 48px 12px;
}

.order-card {
    background: #fff;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 12px;
}

.card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.left-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #2f2f2f;
    font-size: 16px;
    font-weight: 600;
}

.status {
    font-size: 14px;
    color: #46ccdc;
}

.status.unpaid {
    color: #ff8a4c;
}

.line {
    margin-top: 8px;
    color: #7e7e7e;
    font-size: 13px;
}

.course-name {
    margin-top: 6px;
    color: #1d1d1d;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
}

.card-bottom {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.amount-wrap {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
}

.label {
    font-size: 12px;
    color: #b3b3b3;
}

.amount {
    font-size: 20px;
    color: #ff6f4b;
}

.date {
    font-size: 12px;
    color: #b3b3b3;
}

::v-deep .van-button {
    background: #46ccdc;
    border-color: #46ccdc;
    height: 28px;
    padding: 0 14px;
    font-size: 12px;
}

::v-deep .van-button--plain {
    background: transparent;
    color: #46ccdc;
}
</style>
